import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AdminFormValues, adminFormSchema } from "@/types/admin";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { ConsentSection } from "./profile/ConsentSection";
import { BasicInformation } from "./profile/BasicInformation";
import { BackgroundInformation } from "./profile/BackgroundInformation";
import { WorkExperience } from "./profile/WorkExperience";
import { LeadershipConfidence } from "./profile/LeadershipConfidence";
import { ProfileHeader } from "./profile/ProfileHeader";

interface ProfileFormProps {
  initialData?: AdminFormValues;
}

export const ProfileForm = ({ initialData }: ProfileFormProps) => {
  const { toast } = useToast();
  const [isFormVisible, setIsFormVisible] = useState(!initialData);
  const [hasConsent, setHasConsent] = useState(initialData?.has_consent || false);
  const [hasDataConsent, setHasDataConsent] = useState(initialData?.has_data_consent || false);
  
  const form = useForm<AdminFormValues>({
    resolver: zodResolver(adminFormSchema),
    defaultValues: {
      id: "", // This will be overridden with session.user.id
      full_name: "",
      gender: "Annat",
      age: 0,
      languages: [],
      birth_country: "Sverige",
      sweden_arrival_date: null,
      education_level: "Högskola/universitet",
      occupation: "",
      workplace: "",
      previous_group_experience: false,
      previous_groups: null,
      fos_groups_count: 0,
      social_services_knowledge: 3,
      specialization: "",
      years_experience: 0,
      leadership_confidence: {
        equal_knowledge: "Varken eller",
        more_knowledge: "Varken eller",
        different_opinion: "Varken eller",
        disagreement: "Varken eller",
        disinterested: "Varken eller",
        enthusiastic: "Varken eller"
      },
      has_consent: false,
      has_data_consent: false,
      ...initialData,
    },
  });

  const onSubmit = async (values: AdminFormValues) => {
    console.log('Form submission started', { values, hasConsent, hasDataConsent });
    
    if (!hasConsent || !hasDataConsent) {
      console.log('Consent check failed', { hasConsent, hasDataConsent });
      toast({
        title: "Fel",
        description: "Du måste godkänna samtycke för att fortsätta",
        variant: "destructive",
      });
      return;
    }

    console.log('Getting session...');
    const { data: { session } } = await supabase.auth.getSession();
    console.log('Session result:', session?.user?.id);
    
    if (!session?.user?.id) {
      console.log('Session check failed');
      toast({
        title: "Fel",
        description: "Du måste vara inloggad för att uppdatera din profil",
        variant: "destructive",
      });
      return;
    }

    // Exclude id from form values and use session user id instead
    const { id, ...formValues } = values;
    const profileData = {
      id: session.user.id, // Always use session user ID
      ...formValues,
      has_consent: hasConsent,
      has_data_consent: hasDataConsent,
      full_name: values.full_name || "",
      specialization: values.specialization || "",
      years_experience: values.years_experience || 0,
      sweden_arrival_date: values.sweden_arrival_date ? new Date(values.sweden_arrival_date).toISOString() : null,
    };

    console.log('Attempting to upsert profile data:', profileData);
    const { error } = await supabase
      .from('admin_profiles')
      .upsert(profileData);

    console.log('Upsert result:', { error });
    
    if (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Fel",
        description: "Det gick inte att uppdatera profilen",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Klart",
      description: "Profilen har uppdaterats",
    });
    setIsFormVisible(false);
  };

  return (
    <div className="space-y-6 bg-card p-6 rounded-lg shadow-sm">
      <ProfileHeader 
        isFormVisible={isFormVisible} 
        onToggleForm={() => setIsFormVisible(true)} 
      />
      
      {isFormVisible && (
        <>
          <ConsentSection
            hasConsent={hasConsent}
            hasDataConsent={hasDataConsent}
            onConsentChange={setHasConsent}
            onDataConsentChange={setHasDataConsent}
          />

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
              console.log('Form validation errors:', errors);
              toast({
                title: "Validationsfel",
                description: "Vänligen kontrollera att alla fält är korrekt ifyllda",
                variant: "destructive",
              });
            })} className="space-y-6">
              <BasicInformation form={form} />
              <BackgroundInformation form={form} />
              <WorkExperience form={form} />
              <LeadershipConfidence form={form} />
              <Button 
                type="submit" 
                disabled={!hasConsent || !hasDataConsent}
                onClick={(e) => {
                  console.log('Submit button clicked', { hasConsent, hasDataConsent });
                  console.log('Form values:', form.getValues());
                  console.log('Form errors:', form.formState.errors);
                }}
              >
                Spara svar
              </Button>
            </form>
          </Form>
        </>
      )}
    </div>
  );
};